import React, { useRef, useState } from 'react';
import { theme } from 'antd';
import { IconTrash } from '@tabler/icons-react';

interface SwipeToDeleteRowProps {
  /** Controlled: true when this row's delete action is (or is animating to) fully revealed. */
  isOpen: boolean;
  /** Fired on release once the drag settles -- true if past the reveal threshold, false otherwise. Also used to close this row when another one opens (pass isOpen={false} from the parent). */
  onOpenChange: (open: boolean) => void;
  onDeleteClick: () => void;
  deleteLabel: string;
  children: React.ReactNode;
}

const ACTION_WIDTH = 84;
// "suelta pasado el 40-50% del ancho del botón" -- midpoint of that range.
const OPEN_THRESHOLD_RATIO = 0.45;
// Rubber-band damping once the drag goes past either end (closed/fully open) -- makes both limits feel like an edge instead of a hard stop.
const OVERSCROLL_RESISTANCE = 0.35;
// Below this many px of movement, direction isn't decided yet -- avoids
// locking onto a jittery near-zero delta.
const AXIS_LOCK_THRESHOLD_PX = 6;

/**
 * WhatsApp/Gmail/iOS-Mail-style swipe-left-to-reveal-delete for a list row.
 * Built on raw Pointer Events (not framer-motion's drag) so the horizontal
 * swipe and the list's own vertical scroll can be told apart explicitly:
 * `touchAction: 'pan-y'` on the draggable layer lets the browser take native
 * vertical scrolling immediately (delivered to us as a pointercancel, which
 * we treat exactly like a released drag with nothing committed), while the
 * first few px of movement decide the gesture's axis before any transform is
 * applied, so an almost-vertical scroll never twitches sideways first.
 */
const SwipeToDeleteRow: React.FC<SwipeToDeleteRowProps> = ({ isOpen, onOpenChange, onDeleteClick, deleteLabel, children }) => {
  const { token } = theme.useToken();
  // null = not actively dragging right now -- render at the committed
  // (isOpen ? -ACTION_WIDTH : 0) position instead, with a snap transition.
  const [dragX, setDragX] = useState<number | null>(null);

  const pointerIdRef = useRef<number | null>(null);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const startTranslateRef = useRef(0);
  const axisLockRef = useRef<'x' | 'y' | null>(null);
  // True once this gesture has actually moved the row -- lets the
  // synthetic click that follows a drag release be told apart from a real
  // tap, since browsers still fire `click` after a pointer drag.
  const draggedRef = useRef(false);

  const committedX = isOpen ? -ACTION_WIDTH : 0;
  const currentX = dragX ?? committedX;

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    pointerIdRef.current = e.pointerId;
    startXRef.current = e.clientX;
    startYRef.current = e.clientY;
    startTranslateRef.current = committedX;
    axisLockRef.current = null;
    draggedRef.current = false;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== e.pointerId) return;
    const deltaX = e.clientX - startXRef.current;
    const deltaY = e.clientY - startYRef.current;

    if (axisLockRef.current === null) {
      if (Math.abs(deltaX) < AXIS_LOCK_THRESHOLD_PX && Math.abs(deltaY) < AXIS_LOCK_THRESHOLD_PX) return;
      axisLockRef.current = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
      if (axisLockRef.current === 'y') {
        // Vertical gesture -- hand off to the list's own native scroll
        // (already allowed by touchAction: 'pan-y' below) and stop tracking
        // this pointer entirely; no horizontal movement gets applied.
        try {
          e.currentTarget.releasePointerCapture(e.pointerId);
        } catch {
          // Capture may already be gone (e.g. browser released it for us going into the scroll) -- nothing to clean up.
        }
        pointerIdRef.current = null;
        return;
      }
    }
    if (axisLockRef.current !== 'x') return;

    draggedRef.current = true;
    let raw = startTranslateRef.current + deltaX;
    if (raw > 0) raw *= OVERSCROLL_RESISTANCE;
    if (raw < -ACTION_WIDTH) raw = -ACTION_WIDTH + (raw + ACTION_WIDTH) * OVERSCROLL_RESISTANCE;
    setDragX(raw);
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== e.pointerId) return;
    pointerIdRef.current = null;
    if (axisLockRef.current === 'x' && dragX != null) {
      onOpenChange(dragX <= -ACTION_WIDTH * OPEN_THRESHOLD_RATIO);
    }
    setDragX(null);
  };

  // Intercepted in the CAPTURE phase, ahead of the row content's own
  // onClick (e.g. selecting the conversation) -- so a drag-release click and
  // a tap-to-close-while-open click never fall through to it.
  const handleContentClickCapture = (e: React.MouseEvent) => {
    if (draggedRef.current) {
      draggedRef.current = false;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (isOpen) {
      e.preventDefault();
      e.stopPropagation();
      onOpenChange(false);
    }
  };

  return (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      <button
        type="button"
        onClick={onDeleteClick}
        aria-label={deleteLabel}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: ACTION_WIDTH,
          border: 'none',
          background: token.colorError,
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          cursor: 'pointer',
        }}
      >
        <IconTrash size={18} />
        <span style={{ fontSize: 11 }}>{deleteLabel}</span>
      </button>
      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={handleContentClickCapture}
        style={{
          position: 'relative',
          background: token.colorBgContainer,
          transform: `translateX(${currentX}px)`,
          transition: dragX == null ? 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
          touchAction: 'pan-y',
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default SwipeToDeleteRow;
